import { useCallback, useEffect, useRef, useState } from "react";
import {
  bootstrapDeviceSession,
  createOpaqueKey,
  type DeviceSession,
} from "../game/api";

const DEVICE_STORAGE_KEY = "air-hockey.device-identity.v1";
const SESSION_STORAGE_KEY = "air-hockey.device-session.v1";

interface DeviceIdentity {
  deviceId: string;
  deviceSecret: string;
}

export interface DeviceSessionState {
  status: "bootstrapping" | "ready" | "unavailable";
  deviceId: string;
  session: DeviceSession | null;
  error: string | null;
  retry: () => void;
}

export function useDeviceSession(username: string): DeviceSessionState {
  const [identity] = useState<DeviceIdentity>(readOrCreateIdentity);
  const authenticationKeyRef = useRef<string | null>(null);
  const usernameRef = useRef(username);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Omit<DeviceSessionState, "retry">>(() => {
    const cached = readCachedSession();
    return {
      status: cached ? "ready" : "bootstrapping",
      deviceId: identity.deviceId,
      session: cached,
      error: null,
    };
  });
  usernameRef.current = username;

  useEffect(() => {
    if (state.session && isSessionCurrent(state.session)) return;
    if (import.meta.env.PROD && !import.meta.env.VITE_API_URL?.trim()) {
      setState((current) => ({
        ...current,
        status: "unavailable",
        session: null,
        error: "Online services require VITE_API_URL in this deployment.",
      }));
      return;
    }

    const controller = new AbortController();
    const currentIdentity = identity;
    const authenticationKey = authenticationKeyRef.current ?? createOpaqueKey();
    authenticationKeyRef.current = authenticationKey;
    setState((current) => ({ ...current, status: "bootstrapping", error: null }));
    void bootstrapDeviceSession({
      deviceId: currentIdentity.deviceId,
      deviceSecret: currentIdentity.deviceSecret,
      idempotencyKey: authenticationKey,
      username: safeUsername(usernameRef.current, currentIdentity.deviceId),
      signal: controller.signal,
    })
      .then((session) => {
        if (controller.signal.aborted) return;
        authenticationKeyRef.current = createOpaqueKey();
        persistSession(session);
        setState({
          status: "ready",
          deviceId: currentIdentity.deviceId,
          session,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: "unavailable",
          deviceId: currentIdentity.deviceId,
          session: null,
          error: error instanceof Error ? error.message : "Online services are unavailable.",
        });
      });
    return () => controller.abort();
  }, [attempt, identity, state.session]);

  useEffect(() => {
    const invalidate = () => {
      clearPersistedSession();
      setState((current) => ({ ...current, status: "bootstrapping", session: null, error: null }));
      setAttempt((current) => current + 1);
    };
    window.addEventListener("air-hockey:session-invalid", invalidate);
    return () => window.removeEventListener("air-hockey:session-invalid", invalidate);
  }, []);

  const retry = useCallback(() => {
    setState((current) => ({ ...current, session: null }));
    setAttempt((current) => current + 1);
  }, []);

  return { ...state, retry };
}

function readOrCreateIdentity(): DeviceIdentity {
  try {
    const parsed = JSON.parse(localStorage.getItem(DEVICE_STORAGE_KEY) ?? "null") as Partial<DeviceIdentity> | null;
    if (
      parsed &&
      typeof parsed.deviceId === "string" &&
      /^[A-Za-z0-9_-]{43}$/.test(parsed.deviceSecret ?? "")
    ) {
      const identity = {
        deviceId: parsed.deviceId,
        deviceSecret: parsed.deviceSecret as string,
      };
      persistIdentity(identity);
      return identity;
    }
  } catch {
    // A new identity is created below when local storage is empty or invalid.
  }
  const identity: DeviceIdentity = {
    deviceId: crypto.randomUUID(),
    deviceSecret: createOpaqueKey(),
  };
  persistIdentity(identity);
  return identity;
}

function persistIdentity(identity: DeviceIdentity): void {
  try {
    localStorage.setItem(DEVICE_STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // The current page can still use the in-memory identity.
  }
}

function readCachedSession(): DeviceSession | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) ?? "null") as DeviceSession | null;
    return parsed && isSessionCurrent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistSession(session: DeviceSession): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Session remains available until this page closes.
  }
}

function clearPersistedSession(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // The state update still drops the expired in-memory token.
  }
}

function isSessionCurrent(session: DeviceSession): boolean {
  return (
    typeof session.accessToken === "string" &&
    typeof session.expiresAt === "string" &&
    Date.parse(session.expiresAt) > Date.now() + 30_000 &&
    typeof session.player?.id === "string"
  );
}

function safeUsername(username: string, deviceId: string): string {
  const normalized = username
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 17);
  const base = normalized.length >= 3 ? normalized : "Player";
  const suffix = deviceId.replace(/[^A-Za-z0-9]/g, "").slice(-6) || "device";
  return `${base}-${suffix}`.slice(0, 24);
}
